import type { GameState } from "../domain/types/index.js";
import { getMapNode, lookupCard, MAP_EVENTS, ORGANIZATIONS } from "../content/index.js";
import { getRegisteredStageRecord, registerCustomStage } from "../content/customRegistry.js";
import { parseStoredStage, type StoredStage } from "../features/stageMaker/stageRecord.js";
import { toPlayable } from "../features/stageMaker/toPlayable.js";

export const SAVE_KEY = "management-roguelike:save:v1";

export type SavedGame = {
  schemaVersion: 1;
  savedAt: number;
  state: GameState;
  customStage?: StoredStage;
};

let saveWarningShown = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isMapEventEffect(value: unknown): boolean {
  if (!isRecord(value) || typeof value.kind !== "string") return false;
  if (value.kind === "fatigue") return typeof value.amount === "number";
  if (value.kind === "addCard") return typeof value.cardId === "string" && Boolean(lookupCard(value.cardId));
  if (value.kind === "upgradeCard") {
    return typeof value.from === "string" && Boolean(lookupCard(value.from))
      && typeof value.to === "string" && Boolean(lookupCard(value.to));
  }
  if (value.kind === "nextCase") {
    return (value.stat === "deadline" || value.stat === "required" || value.stat === "energy")
      && typeof value.amount === "number";
  }
  return false;
}

function isSavedGame(value: unknown): value is SavedGame {
  if (!isRecord(value)) return false;
  return value.schemaVersion === 1 && typeof value.savedAt === "number" && isRecord(value.state);
}

function hasKnownCards(state: GameState): boolean {
  const run = state.run;
  if (!run) return true;
  if (!isRecord(run)
    || run.schemaVersion !== 1
    || !Array.isArray(run.deck)
    || !Array.isArray(run.hand)
    || !Array.isArray(run.discard)
    || !Array.isArray(run.inPlay)
    || !Array.isArray(run.aside)
    || (run.caseExcluded !== undefined && !Array.isArray(run.caseExcluded))
    || (run.actionDrawRemaining !== undefined && (!Number.isInteger(run.actionDrawRemaining) || run.actionDrawRemaining < 0))
    || !Array.isArray(run.organizations)
    || !Array.isArray(run.availableNodeIds)
    || !Array.isArray(run.clearedNodeIds)
    || !Number.isInteger(run.rngState)
    || run.rngState < 0
    || run.rngState > 0xffffffff
    || !Number.isInteger(run.nextCardSequence)
    || run.nextCardSequence < 1
    || !Number.isInteger(run.fatigue)
    || run.fatigue < 0) return false;
  const cards = [run.deck, run.hand, run.discard, run.inPlay, run.aside, run.caseExcluded ?? []].flat();
  if (!cards.every((card) => isRecord(card) && typeof card.instanceId === "string" && typeof card.cardId === "string" && lookupCard(card.cardId)
    && (card.temporaryForCase === undefined || typeof card.temporaryForCase === "boolean"))) return false;
  const ids = cards.map((card) => card.instanceId);
  const stageId = typeof run.stageId === "string" ? run.stageId : undefined;
  return new Set(ids).size === ids.length
    && run.organizations.every((id) => ORGANIZATIONS[id as keyof typeof ORGANIZATIONS])
    && Boolean(getMapNode(stageId, run.currentNodeId))
    && run.availableNodeIds.every((id) => getMapNode(stageId, id))
    && run.clearedNodeIds.every((id) => getMapNode(stageId, id));
}

function isRestorableState(value: unknown): value is GameState {
  const screens = new Set(["title", "stageSelect", "map", "case.play", "case.resolve", "reward.card", "reward.organization", "rest", "event", "result.clear", "result.gameover"]);
  const overlays = new Set([
    "none",
    "deck",
    "log",
    "newRunConfirm",
    "abandonConfirm",
    "endPhaseConfirm",
    "skipSelectionConfirm",
    "skipFieldJudgmentConfirm",
    "phaseTutorial",
    "statHelp.progress",
    "statHelp.deadline",
    "statHelp.fatigue",
    "statHelp.block",
  ]);
  if (!isRecord(value) || !screens.has(value.screen as string) || !overlays.has(value.overlay as string) || !("run" in value)) return false;
  if (value.run !== null && !isRecord(value.run)) return false;
  if (value.screen === "event") {
    const activeEvent = value.activeEvent;
    if (!isRecord(activeEvent) || typeof activeEvent.eventId !== "string" || typeof activeEvent.nodeId !== "string" || typeof activeEvent.effectText !== "string" || !Array.isArray(activeEvent.effects)) return false;
    if (!activeEvent.effects.every(isMapEventEffect)) return false;
    if (!MAP_EVENTS.some((event) => event.id === activeEvent.eventId)) return false;
    if (!isRecord(value.run)) return false;
    const node = getMapNode(typeof value.run.stageId === "string" ? value.run.stageId : undefined, activeEvent.nodeId);
    if (node?.kind !== "event") return false;
  }
  return hasKnownCards(value as GameState);
}

export function hasSave(): boolean {
  try {
    return localStorage.getItem(SAVE_KEY) !== null;
  } catch {
    return false;
  }
}

export function loadGame(): GameState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isSavedGame(parsed)) {
      localStorage.removeItem(SAVE_KEY);
      return null;
    }
    if (parsed.customStage !== undefined) {
      const record = parseStoredStage(parsed.customStage);
      const run = isRecord(parsed.state.run) ? parsed.state.run : null;
      if (!record || run?.stageId !== record.id) {
        localStorage.removeItem(SAVE_KEY);
        return null;
      }
      const playable = toPlayable(record);
      registerCustomStage(playable.stage, playable.cases, record);
    }
    if (!isRestorableState(parsed.state)) {
      localStorage.removeItem(SAVE_KEY);
      return null;
    }
    // 古いセーブには後発のフィールドがないので、既定値を補って復元する。
    return { ...parsed.state, bringInStageId: parsed.state.bringInStageId ?? null };
  } catch {
    localStorage.removeItem(SAVE_KEY);
    return null;
  }
}

export function saveGame(state: GameState): boolean {
  try {
    const customStage = getRegisteredStageRecord(state.run?.stageId);
    const payload: SavedGame = {
      schemaVersion: 1,
      savedAt: Date.now(),
      state,
      ...(customStage ? { customStage } : {}),
    };
    localStorage.setItem(SAVE_KEY, JSON.stringify(payload));
    return true;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("save failed", error);
    } else if (!saveWarningShown) {
      saveWarningShown = true;
      console.warn("この端末には進行を保存できません");
    }
    return false;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* ignore */
  }
}

export function shouldPersist(state: GameState): boolean {
  if (!state.run) return false;
  if (state.screen === "title" || state.screen === "stageSelect") return false;
  return true;
}
